import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { DATA_KEYS, EMPTY_VALUE, TONE } from '../../data/constants'
import { getDataGap } from '../../data/reportModel'
import { getCapabilityGap } from '../../data/scoring'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'

const getGroupLifecyclePolicyLifetimeDays = (tenant) => {
  const lifetimeDays = tenant?.groupLifecyclePolicyLifetimeDays
  return Number.isFinite(lifetimeDays) ? lifetimeDays : null
}

const getTableFooter = ({ shown, total }) => {
  if (shown === 0 || total <= shown) return null

  return <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.TABLE_FOOTER" values={{ shown, total }} />
}

export default function GroupGovernance({
  summary,
  tenant = null,
  dynamicGroups,
  totalRows = dynamicGroups.length,
  score,
  gap = null,
  partialGaps = [],
  dataAvailability = [],
}) {
  const intl = useIntl()
  const dynamicWithRules = summary.groups?.dynamicWithMembershipRules ?? 0
  const dynamicCanIncludeGuests = summary.groups?.dynamicCanIncludeGuests ?? 0
  const dynamicGroupRows = dynamicGroups.map((group) => ({ ...group, key: group.entityId }))
  const groupLifecyclePolicyLifetimeDays = getGroupLifecyclePolicyLifetimeDays(tenant)
  const groupLifecycleGap = gap ?? getCapabilityGap(partialGaps, 'groupExpiration')
  const dynamicGroupsGap = gap ?? getCapabilityGap(partialGaps, 'dynamicGroupGuests')
  const groupLifecyclePolicyIsAssessed = !groupLifecycleGap
  const groupsGap = getDataGap(dataAvailability, [DATA_KEYS.GROUPS])
  let guestInclusionTone = TONE.NEUTRAL
  if (!dynamicGroupsGap) guestInclusionTone = dynamicCanIncludeGuests > 0 ? TONE.DANGER : TONE.OK
  const hasNoGroupLifecyclePolicy = groupLifecyclePolicyIsAssessed && groupLifecyclePolicyLifetimeDays === null
  let groupLifecyclePolicyValue = EMPTY_VALUE

  if (groupLifecyclePolicyLifetimeDays !== null) {
    groupLifecyclePolicyValue = (
      <FormattedMessage
        id="AI_RISK_REPORT.DATA.GROUPS.STAT_LIFECYCLE_DAYS"
        values={{ days: groupLifecyclePolicyLifetimeDays }}
      />
    )
  } else if (hasNoGroupLifecyclePolicy) {
    groupLifecyclePolicyValue = <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_LIFECYCLE_NONE" />
  }

  const stats = [
    {
      key: 'total',
      value: groupsGap ? EMPTY_VALUE : (summary.groups?.microsoft365 ?? EMPTY_VALUE),
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_TOTAL" />,
    },
    {
      key: 'expirationPolicy',
      value: groupLifecyclePolicyValue,
      tone: hasNoGroupLifecyclePolicy ? TONE.DANGER : undefined,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_LIFECYCLE_LABEL" />,
    },
    {
      key: 'guestInclusion',
      value: dynamicGroupsGap ? EMPTY_VALUE : dynamicCanIncludeGuests,
      denom: dynamicGroupsGap ? undefined : dynamicWithRules,
      tone: guestInclusionTone,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_GUEST_INCL" />,
    },
  ]

  const columns = [
    {
      key: 'group',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_GROUP" />,
      render: (row) => <span className="text-body fw-bold">{row.displayName}</span>,
      sortable: true,
      sortValue: (row) => (row.displayName || '').toLowerCase(),
      searchValue: (row) => [row.displayName, row.mail, row.mailNickname, row.id, row.entityId],
    },
    {
      key: 'members',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_MEMBERS" />,
      render: (row) => row.memberCount ?? '-',
      sortable: true,
      sortValue: (row) => row.memberCount ?? -1,
      searchValue: (row) => row.memberCount ?? '-',
    },
    {
      key: 'rule',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_RULE" />,
      render: (row) => (row.membershipRule ? <code>{row.membershipRule}</code> : '-'),
      sortable: true,
      sortValue: (row) => row.membershipRule || '',
      searchValue: (row) => row.membershipRule || '-',
    },
    {
      key: 'guests',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_GUESTS" />,
      render: (row) => (
        <Chip tone={row.canIncludeGuests ? TONE.DANGER : TONE.OK}>
          <FormattedMessage id={row.canIncludeGuests ? 'AI_RISK_REPORT.LABEL.NO' : 'AI_RISK_REPORT.LABEL.YES'} />
        </Chip>
      ),
      sortable: true,
      sortValue: (row) => (row.canIncludeGuests ? 1 : 0),
      searchValue: (row) =>
        intl.formatMessage({ id: row.canIncludeGuests ? 'AI_RISK_REPORT.LABEL.NO' : 'AI_RISK_REPORT.LABEL.YES' }),
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DATA.GROUPS.TITLE"
      subtitleId="AI_RISK_REPORT.DATA.GROUPS.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      partialGap={partialGaps[0]}
      stats={stats}
    >
      <ReportTable
        columns={columns}
        rows={dynamicGroupRows}
        totalRows={totalRows}
        affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.DYNAMIC_GROUPS"
        isAffectedEntityTable={false}
        footer={getTableFooter({ shown: dynamicGroupRows.length, total: dynamicWithRules })}
        empty={<EmptyState messageId="AI_RISK_REPORT.NO_DYNAMIC_GROUPS" />}
      />
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  groups: PropTypes.shape({
    total: PropTypes.number,
    microsoft365: PropTypes.number,
    dynamicWithMembershipRules: PropTypes.number,
    dynamicCanIncludeGuests: PropTypes.number,
  }),
})

GroupGovernance.propTypes = {
  summary: summaryPropType.isRequired,
  tenant: PropTypes.shape({
    groupLifecyclePolicyLifetimeDays: PropTypes.number,
  }),
  dynamicGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalRows: PropTypes.number,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string }),
  partialGaps: PropTypes.arrayOf(
    PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string, capabilityId: PropTypes.string }),
  ),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
}
